
<script setup>

  import { ref, computed } from 'vue';

  import { useRouter, useRoute } from 'vue-router';

  import { useResizeObserver } from '@vueuse/core';

  import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';

  import { Bars3Icon, XMarkIcon, ChevronDownIcon, ChevronUpIcon, ArrowPathIcon, ArrowRightCircleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';

  import { HomeIcon } from '@heroicons/vue/24/solid';

  import ClearDialog from './dialogs/clear.vue';

  import SimpleDialog from './dialogs/simple.vue';

  import { clearStoredRefs } from './stores/utils.js';

  import headerBackground from './images/header-background.jpg';

  const main = ref( null );

  const router = useRouter();

  const route = useRoute();

  const header = ref( null );

  const buttons = ref( null );

  const clearDialog = ref( null );

  const creditsDialog = ref( null );

  const activities = [

    { name: 'Hearing All the Stories' },

    { name: 'Jim Crow Era Traveling' },

    { name: 'The Essence of Life at Lewis' },

    { name: 'Identifying Agents of Change' },

    { name: 'Telling the Whole Story' }

  ].map( ( props, index ) => ( { ...props, id: index + 1 } ) );

  const startOver = () => 

    clearDialog.value
    
      .ask()
    
      .then( selection => {

        clearStoredRefs( selection );

        router.push( { path: '/activities' } );

      } )

      .catch( () => {

        // NOOP

      } );

  const showNavigation = computed( () => route.path !== '/' && route.path !== '/introduction' );

  useResizeObserver( header, entries => {

    const { height } = header.value.getBoundingClientRect();

    document.documentElement.style.setProperty( '--header-height', `${ height }px` );

  } );

</script>

<template>

  <button type="button" class="sr-only focus:not-sr-only px-2 py-4 text-white border-none" @click="main.focus()">Skip to content.</button>

  <header ref="header" class="sticky top-0 z-10 drop-shadow-[0_0_5px_rgba(0,0,0,1)]">

    <div class="flex items-center h-16 text-white bg-black bg-no-repeat bg-right"
    
      :style="{ 'background-image': `url( ${ headerBackground } )` }">

      <img class="ml-6 w-8" draggable="false" src="./images/logomark.svg" alt="National Park Service" />

      <h1 class="ml-6 text-sm leading-4">

        <div class="font-bold">
          
          Shenandoah
          
          <span class="md:hidden">Nat'l</span>

          <span class="hidden md:inline">National</span>
          
          Park
        
        </div>

        <div class="font-semibold">Lewis Mountain</div>

      </h1>

      <Menu as="div" class="md:relative ml-auto md:ml-12 h-full" v-slot="{ open }" v-if="showNavigation">

        <MenuButton class="min-w-[64px] h-full text-black bg-amber-300 transition-colors hover:bg-amber-400 focus-visible:bg-amber-400 hover:underline focus-visible:underline">

          <div class="md:hidden px-4">

            <XMarkIcon class="w-8" v-if="open" />

            <Bars3Icon class="w-8" v-if="! open" />

          </div>

          <div class="hidden md:flex items-center px-5 gap-2">

            <span class="font-semibold text-sm">Menu</span>

            <ChevronUpIcon class="w-5" v-if="open" />

            <ChevronDownIcon class="w-5" v-if="! open" />

          </div>

        </MenuButton>

        <MenuItems class="overflow-hidden absolute left-0 z-10 w-full md:w-auto divide-y divide-neutral-800 text-sm bg-black">

          <MenuItem as="button" v-slot="{ active }" class="flex items-center w-full gap-4 h-16 px-5" @click="router.push( { path: '/activities' } )">
        
            <HomeIcon :class="[ 'w-5', { 'text-amber-300': active } ]" />

            <span :class="{ 'underline': active }">Home</span>
          
          </MenuItem>

          <MenuItem as="button" v-for="{ id, name } in activities" v-slot="{ active }" class="flex items-center w-full gap-4 h-16 px-5 text-left"
            
            @click="router.push( { path: `/activity/${ id }` } )">

            <ArrowRightCircleIcon :class="[ 'w-5', { 'text-amber-300': active } ]" />

            <div>

              <div :class="[ 'uppercase font-bold text-xs', { 'text-amber-300': active } ]">Activity {{ id }}</div>

              <div :class="[ 'whitespace-nowrap', { 'underline': active } ]">{{ name }}</div>

            </div>
          
          </MenuItem>

          <MenuItem as="button" v-slot="{ active }" class="flex md:hidden items-center w-full gap-4 h-16 px-5 text-left"

            @click="creditsDialog.open()">

            <InformationCircleIcon :class="[ 'w-5', { 'text-amber-300': active } ]" />

            <span :class="{ 'underline': active }">Credits</span>

          </MenuItem>

          <MenuItem as="button" v-slot="{ active }" class="flex items-center w-full gap-4 h-16 px-5" @click="startOver">
          
            <ArrowPathIcon :class="[ 'w-5', { 'text-amber-300': active } ]" />

            <span :class="[ 'whitespace-nowrap', { 'underline': active } ]">Clear &amp; Start Over </span>
          
          </MenuItem>

        </MenuItems>

      </Menu>

      <div ref="buttons" class="hidden md:block h-full" v-if="showNavigation" />

      <button class="hidden md:flex items-center px-5 gap-2 h-full hover:underline focus-visible:underline ml-auto mr-6" @click="creditsDialog.open()">
        
        <InformationCircleIcon class="w-5" />

        <span class="text-sm">Credits</span>
      
      </button>

    </div>

  </header>

  <main ref="main" class="grid" tabindex="0">
    
    <RouterView v-slot="{ Component }">
  
      <Transition

        enter-from-class="opacity-0"

        leave-to-class="opacity-0"

        enter-active-class="transition duration-300"

        leave-active-class="transition duration-300">

        <component :is="Component" class="row-start-1 col-start-1 min-h-view" :header="header" :buttons="buttons" />
        
      </Transition>
      
    </RouterView>
    
  </main>

  <ClearDialog ref="clearDialog" />

  <SimpleDialog ref="creditsDialog" title="Credits" class="max-w-3xl">

    <p class="leading-7 text-neutral-700">

      This program was created by the Shenandoah National Park Media Team with funding from your fee dollars.

    </p>

    <p class="mt-6 leading-7 text-neutral-700">

      Funding for research was generously donated by the Appalachian Mountain Club.

    </p>

    <p class="mt-6 leading-7 text-neutral-700">

      We are grateful to the many people and organizations who contributed to this project:

    </p>

    <ul class="list-disc mt-6 pl-8 space-y-3 text-neutral-700">

      <li>Audrey Tutt Smith</li>

      <li>Elaine Taylor Blakey</li>

      <li>Bobby Brown</li>

      <li>Columbus Tutt, Jr.</li>

      <li>Elaine Rita Tutt</li>

      <li>Craig Tutt</li>

      <li>Beulah Billings</li>

      <li>Robert Billings</li>

      <li>Shirley Ann Tutt McCoy</li>

      <li>Maxine Tutt</li>

      <li>Lloyd Tutt</li>

      <li>Mittie Tutt</li>

      <li>Reed Engle</li>

      <li>Erin Krutko Devlin</li>

      <li>Jacob Torkelson</li>

      <li>Sarah Babcock</li>

      <li>Roy Wood</li>

      <li>Jim Schaberl</li>

      <li>Kandace Muller</li>

      <li>The Rec</li>

      <li>Living Legacy, Luray, VA</li>

      <li>New York-Virginia Club</li>

      <li>MacMillan Podcasts</li>

      <li>National Museum of African American History and Culture</li>

      <li>Library of Congress</li>

    </ul>

    <p class="mt-6 leading-7 text-neutral-700">

      Site design and functionality by A&T Integrated, Inc.

    </p>

  </SimpleDialog>

</template>
